import React, { useEffect, useState, useRef } from "react";
import {
  getAuth,
  confirmPasswordReset,
  verifyPasswordResetCode,
  applyActionCode,
  updateEmail,
} from "firebase/auth";
import { useNavigate, useSearchParams } from "react-router-dom";
import MarketingNavbar from "../components/marketingNav";
import { useAuthState } from "react-firebase-hooks/auth";
import { auth, db } from "../services/firebase";
import { doc, updateDoc, query, collection, where, getDocs } from "firebase/firestore";
import { toast } from "react-hot-toast";

export default function PasswordResetConfirmation() {
  const [searchParams] = useSearchParams();
  const oobCode = searchParams.get("oobCode"); // Firebase sends this as a query param
  const mode = searchParams.get("mode"); // Get the mode parameter
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [user, authLoading, error] = useAuthState(auth);
  const hasAttemptedVerification = useRef(false);

  const navigate = useNavigate();

  // Only run auth state check once
  useEffect(() => {
    if (authLoading) return;
  }, [authLoading]);

  // Handle verification only once when component mounts
  useEffect(() => {
    const verifyEmail = async () => {
      if (!oobCode || hasAttemptedVerification.current) return;

      hasAttemptedVerification.current = true;

      if (mode === "verifyEmail") {
        await handleEmailVerification();
      } else if (mode === "verifyAndChangeEmail") {
        await handleEmailChangeVerification();
      }
    };

    verifyEmail();
  }, [oobCode, mode]); // Only depend on oobCode and mode

  const handleEmailVerification = async () => {
    setLoading(true);
    try {
      await applyActionCode(auth, oobCode);
      setMessage("Email verified successfully! You can now log in.");
      setTimeout(() => {
        //navigate("/login");
      }, 3000);
      if (auth.currentUser.emailVerified === true) {
        navigate("/my-account");
      }
    } catch (err) {
      toast.error(err.message);
      //navigate("/login");
    } finally {
      setLoading(false);
    }
  };

  const handleEmailChangeVerification = async () => {
    if (hasAttemptedVerification.current && loading) return; // Prevent duplicate execution
    setLoading(true);
    console.log("attempting to verify email change");
    try {
      // First verify the action code is valid before proceeding
      try {
        await verifyPasswordResetCode(auth, oobCode);
        console.log("action code verified");
      } catch (verifyError) {
        console.log("verification error:", verifyError.code);
        if (verifyError.code === 'auth/invalid-action-code') {
          toast.error("This email verification link has expired or is invalid. Please request a new email change.");
          console.log("action code invalid");
          setTimeout(() => {
            window.location.href = "/my-account";
          }, 3000);
          return;
        }
        throw verifyError;
      }

      // Check if user is logged in
      const currentUser = auth.currentUser;
      if (!currentUser) {
        console.log("no user found - redirecting to login");
        toast.error("Please log in to complete the email change.");
        setTimeout(() => {
          window.location.href = "/login";
        }, 3000);
        return;
      }

      // Check if the token is expired
      try {
        const idTokenResult = await currentUser.getIdTokenResult();
        if (idTokenResult.expirationTime < new Date().toISOString()) {
          console.log("token expired - redirecting to login");
          toast.success("Email verified! Please log in again to complete the change.");
          setTimeout(() => {
            window.location.href = "/login";
          }, 3000);
          return;
        }
      } catch (tokenError) {
        console.log("token error:", tokenError);
        toast.error("Please log in again to complete the email change.");
        setTimeout(() => {
          window.location.href = "/login";
        }, 3000);
        return;
      }

      console.log("user found and token valid");

      // Apply the action code to verify the email change
      await applyActionCode(auth, oobCode);
      console.log("action code applied");

      // Get the new email from the action code

      // Update the email in Firebase Auth
      // await updateEmail(currentUser, newEmail);
      // console.log("email updated in auth");

      // Wait a moment to ensure Auth is updated
      await new Promise(resolve => setTimeout(resolve, 1000));
      await currentUser.reload(); // Force refresh to get the new email


      setMessage("Email updated successfully! You can now log in with your new email address.");
      toast.success("Email updated successfully! You can now log in with your new email address.");
      setTimeout(() => {
        window.location.href = "/login";
      }, 3000);
    } catch (err) {
      console.error("Email change verification error:", err);
      if (err.code === 'auth/requires-recent-login' || err.code === 'auth/user-token-expired') {
        toast("Your session has expired. Please log in again to complete the email change.");
        setTimeout(() => {
          window.location.href = "/login";
        }, 3000);
      } else if (err.code === 'auth/invalid-action-code') {
        toast.error("This email verification link has expired or is invalid. Please request a new email change.");
        setTimeout(() => {
          window.location.href = "/my-account";
        }, 3000);
      } else {
        toast.error(err.message || "Failed to verify email change");
        setTimeout(() => {
          window.location.href = "/my-account";
        }, 3000);
      }
    } finally {
      setLoading(false);
    }
  };

  const HandlePasswordReset = async (e) => {
    e.preventDefault();
    setLoading(true);

    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match.");
      setLoading(false);
      return;
    }

    const auth = getAuth();

    try {
      // Verify the reset code
      await verifyPasswordResetCode(auth, oobCode);

      // Confirm the password reset
      await confirmPasswordReset(auth, oobCode, newPassword);
      setMessage(
        "Your password has been reset successfully. You can now log in with your new password."
      );
      setTimeout(() => {
        //navigate("/login");
      }, 3000);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  // If we're handling email verification or email change, show a simpler UI
  if (mode === "verifyEmail" || mode === "verifyAndChangeEmail") {
    return (
      <>
        <MarketingNavbar />
        <div style={{ maxWidth: "400px", margin: "0 auto", textAlign: "center", padding: "2rem" }}>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            {mode === "verifyAndChangeEmail" ? "Email Change Verification" : "Email Verification"}
          </h2>
          {message ? (
            <div className="mt-4 p-4 bg-green-50 rounded-md">
              <p className="text-green-800">{message}</p>
            </div>
          ) : (
            <div className="mt-4">
              <p className="text-gray-600">
                {mode === "verifyAndChangeEmail"
                  ? "Verifying your new email address..."
                  : "Verifying your email..."}
              </p>
              {loading && (
                <div className="mt-4">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
                </div>
              )}
            </div>
          )}
          {error && (
            <p className="mt-4 text-red-600">{error}</p>
          )}
        </div>
      </>
    );
  }

  // Password reset UI
  return (
    <>
      <MarketingNavbar />
      <div style={{ maxWidth: "400px", margin: "0 auto", textAlign: "center", padding: "2rem" }}>
        <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
          Password Reset
        </h2>
        {message ? (
          <div className="mt-4 p-4 bg-green-50 rounded-md">
            <p className="text-green-800">{message}</p>
          </div>
        ) : (
          <form onSubmit={HandlePasswordReset} className="mt-8 space-y-6">
            <div>
              <label
                htmlFor="newPassword"
                className="block text-sm font-medium text-gray-700"
              >
                New Password
              </label>
              <input
                type="password"
                id="newPassword"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter your new password"
                required
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label
                htmlFor="confirmPassword"
                className="block text-sm font-medium text-gray-700"
              >
                Confirm Password
              </label>
              <input
                type="password"
                id="confirmPassword"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm your new password"
                required
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50"
            >
              {loading ? "Processing..." : "Reset Password"}
            </button>
          </form>
        )}
        {error && (
          <p className="mt-4 text-red-600">{error}</p>
        )}
      </div>
    </>
  );
}
